/**
 * mediaState — os três estados possíveis do arquivo de uma mensagem de mídia.
 *
 * Antes desta mudança, "mensagem de mídia sem arquivo" só podia significar
 * FALHA, e a condição `!mediaUrl && FAILED_MEDIA_TYPES.includes(mediaType)`
 * estava duplicada em quatro pontos do MessagesList. Agora existe um estado
 * intermediário legítimo — o arquivo está BAIXANDO em background — e a
 * duplicação viraria três bugs em vez de um.
 *
 * Funções puras, sem React, no precedente de `utils/messagePreview.js`.
 */

/**
 * Tipos que reservam espaço de mídia na bolha.
 *
 * Antes se chamava FAILED_MEDIA_TYPES, mas o nome passou a mentir: a lista
 * responde "esta bolha tem um slot de mídia?", não "esta mídia falhou?".
 */
export const MEDIA_SLOT_TYPES = [
  "image",
  "video",
  "audio",
  "document",
  "sticker",
  "stickerMessage",
  "media"
];

export const MEDIA_STATUS_PENDING = "pending";
export const MEDIA_STATUS_DOWNLOADING = "downloading";
export const MEDIA_STATUS_FAILED = "failed";
export const MEDIA_STATUS_DONE = "done";

/**
 * Depois disto, um "baixando" sem desfecho vira falha na tela.
 *
 * Um emit perdido é indistinguível de um download lento do lado do cliente, e
 * sem este teto o atendente ficaria olhando um spinner eterno sem nem o botão
 * de tentar novamente. O backend tem a sua própria expiração (24 h, no janitor);
 * esta aqui é só a proteção da interface.
 */
export const MEDIA_DOWNLOAD_STALE_MINUTES = 10;

export const getMediaDownloadStatus = (message) =>
  String(message?.mediaDownloadStatus || "").trim().toLowerCase();

/** "A bolha reserva espaço de mídia mas ainda não tem arquivo." */
export const isMediaSlotEmpty = (message, resolvedUrl) =>
  !resolvedUrl && MEDIA_SLOT_TYPES.includes(message?.mediaType);

export const isMediaDownloadStale = (message) => {
  const raw = message?.createdAt;
  if (!raw) return false;

  const createdAt = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(createdAt.getTime())) return false;

  return Date.now() - createdAt.getTime() > MEDIA_DOWNLOAD_STALE_MINUTES * 60 * 1000;
};

/**
 * Está baixando agora.
 *
 * Exige `!resolvedUrl` de propósito, e não só o status: é defesa em profundidade
 * contra o merge do reducer deixar um status velho preso no estado local. Uma
 * mensagem que JÁ TEM arquivo nunca mostra spinner, aconteça o que acontecer
 * com o campo de status.
 */
export const isMediaDownloading = (message, resolvedUrl) =>
  isMediaSlotEmpty(message, resolvedUrl) &&
  [MEDIA_STATUS_PENDING, MEDIA_STATUS_DOWNLOADING].includes(
    getMediaDownloadStatus(message)
  ) &&
  !isMediaDownloadStale(message);

/**
 * Falhou — definido como o COMPLEMENTO de "baixando", não como um valor próprio.
 *
 * É o que dá retrocompatibilidade de graça: mensagens antigas, que falharam
 * antes desta mudança e têm `mediaDownloadStatus` nulo, continuam caindo aqui e
 * renderizando exatamente como sempre renderizaram.
 */
export const isMediaFailed = (message, resolvedUrl) =>
  isMediaSlotEmpty(message, resolvedUrl) &&
  !isMediaDownloading(message, resolvedUrl);

/** Miniatura embutida do WhatsApp, quando o backend conseguiu guardá-la. */
export const getMediaThumbUrl = (message) => message?.mediaThumbUrl || null;
